/**
 * Order reviews / ratings API endpoints
 */
import { api } from './baseApi';
import type { OrderReview, ReviewStatistics } from '../../types/review';

interface PaginatedResponse<T> {
  success: boolean;
  count: number;
  next: string | null;
  previous: string | null;
  total_pages: number;
  current_page: number;
  results: T[];
}

export interface ReviewsParams {
  page?: number;
  page_size?: number;
  search?: string;
  rating?: number;
  max_rating?: number;
  is_hidden?: boolean;
  source?: 'APP' | 'DASHBOARD';
  provider_type?: 'SPECIALIST' | 'CONSULTANT';
  provider_id?: number;
  patient_id?: number;
}

export const reviewsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getReviews: builder.query<PaginatedResponse<OrderReview>, ReviewsParams>({
      query: (params) => ({ url: '/dashboard/reviews/', params }),
      providesTags: ['Reviews'],
    }),
    getReviewStatistics: builder.query<{ data: ReviewStatistics }, void>({
      query: () => '/dashboard/reviews/statistics/',
      providesTags: ['Reviews'],
    }),
    updateReview: builder.mutation<
      { data: OrderReview },
      { id: number; is_hidden?: boolean; admin_notes?: string }
    >({
      query: ({ id, ...body }) => ({ url: `/dashboard/reviews/${id}/`, method: 'PATCH', body }),
      invalidatesTags: ['Reviews', 'Order', 'Orders', 'Specialists', 'Consultants'],
    }),
    submitOrderReview: builder.mutation<
      { data: OrderReview },
      { order_id: number; service_rating: number; provider_rating?: number | null; comment?: string }
    >({
      query: ({ order_id, ...body }) => ({
        url: `/dashboard/orders/${order_id}/review/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { order_id }) => [
        { type: 'Order', id: order_id },
        'Orders',
        'Reviews',
        'Specialists',
        'Consultants',
      ],
    }),
  }),
});

export const {
  useGetReviewsQuery,
  useGetReviewStatisticsQuery,
  useUpdateReviewMutation,
  useSubmitOrderReviewMutation,
} = reviewsApi;
